import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { LanguageSwitcher, Wordmark } from '../../atoms';
import { cacheOptions, fetchFirebaseData } from '../../../../connector/functions';
import { useQuery } from '@tanstack/react-query';

const Navigation = () => {
    const { language } = useLanguage();
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const { data: navItemsData } = useQuery({
        queryKey: ['navItems', language],
        queryFn: () => fetchFirebaseData(`site/${language}/navigation`),
        ...cacheOptions,
    });

    const navItems = (() => {
        if (!navItemsData) return [];
        const items = Array.isArray(navItemsData)
            ? navItemsData
            : Object.values(navItemsData);
        return [...items].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
    })();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const sectionIds = navItems.map((item: any) => item.id).filter(Boolean);
        if (!sectionIds.length) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
                if (visible?.target?.id) {
                    setActiveSection(visible.target.id);
                }
            },
            { rootMargin: '-35% 0px -50% 0px', threshold: [0.1, 0.35, 0.6] },
        );

        sectionIds.forEach((id: string) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [navItems]);

    const scrollToSection = (href: string) => {
        const element = document.querySelector(href);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        setIsMobileMenuOpen(false);
    };

    return (
        <nav
            className={`fixed left-0 right-0 top-0 z-50 transition-colors duration-300 ${
                isScrolled || isMobileMenuOpen
                    ? 'border-b border-white/5 bg-ink/85 backdrop-blur-md'
                    : 'bg-transparent'
            }`}
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                    <a
                        href="#home"
                        onClick={(e) => {
                            e.preventDefault();
                            scrollToSection('#home');
                        }}
                        className="transition-opacity hover:opacity-80"
                    >
                        <Wordmark size="nav" />
                    </a>

                    <div className="hidden items-center gap-8 md:flex">
                        {navItems.map((item: any) => (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    scrollToSection(`#${item.id}`);
                                }}
                                className={`relative pb-1 text-sm font-medium transition-colors duration-300 ${
                                    activeSection === item.id
                                        ? 'text-accent'
                                        : 'text-muted hover:text-fg'
                                }`}
                            >
                                {item.name}
                                <span
                                    className={`absolute bottom-0 left-0 h-0.5 bg-accent transition-all duration-300 ${
                                        activeSection === item.id ? 'w-full' : 'w-0'
                                    }`}
                                    aria-hidden="true"
                                />
                            </a>
                        ))}
                        <LanguageSwitcher />
                    </div>

                    <button
                        type="button"
                        className="inline-flex items-center justify-center p-2 text-fg md:hidden"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label="Toggle menu"
                        aria-expanded={isMobileMenuOpen}
                    >
                        <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                            {isMobileMenuOpen ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            )}
                        </svg>
                    </button>
                </div>
            </div>

            {isMobileMenuOpen && (
                <div className="border-t border-white/5 bg-surface md:hidden">
                    <div className="space-y-1 px-4 py-4">
                        {navItems.map((item: any) => (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    scrollToSection(`#${item.id}`);
                                }}
                                className={`block py-2 text-base font-medium ${
                                    activeSection === item.id ? 'text-accent' : 'text-muted hover:text-fg'
                                }`}
                            >
                                {item.name}
                            </a>
                        ))}
                        <div className="pt-2">
                            <LanguageSwitcher />
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export { Navigation };
